import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import encountersService, {
  SOAPNoteListItem,
} from "../../services/encountersService";
import AppLayout from "../../shared/components/AppLayout";

const EncountersListPage: React.FC = () => {
  const [encounters, setEncounters] = useState<SOAPNoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10; // Matches backend PAGE_SIZE
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");


  useEffect(() => {
    loadEncounters();
  }, [currentPage]);

  const loadEncounters = async () => {
    try {
      console.log(
        `🔍 [Encounters] Starting to load encounters for page ${currentPage}...`,
      );
      setLoading(true);
      setError(null);

      const response = await encountersService.fetchDoctorEncounters({
        page: currentPage,
      });
      console.log("✅ [Encounters] API Response:", response);
      console.log(
        "📊 [Encounters] Number of results:",
        response.results?.length || 0,
      );

      setEncounters(response.results || []);
      setTotalCount(response.count || 0);
      setError(null);
    } catch (err: any) {
      console.error("❌ [Encounters] Error loading encounters:", err);
      console.error("❌ [Encounters] Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });

      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        "Failed to load encounters";
      setError(errorMessage);
    } finally {
      setLoading(false);
      console.log("🏁 [Encounters] Load complete");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <AppLayout active="encounters">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Patient Encounters
                </h1>
                <p className="text-gray-500 mt-1">
                  Manage and review your medical conversation notes
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                      Total Encounters
                    </p>
                    <p className="text-xl font-bold text-gray-900 leading-none">
                      {totalCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {loading && currentPage === 1 && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-500 font-medium">
                  Loading encounters...
                </p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center text-red-600 shrink-0">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-red-800 font-bold">Error Loading Data</h3>
                  <p className="text-red-600 mb-3">{error}</p>
                  <button
                    onClick={() => navigate("/encounters/debug")}
                    className="inline-flex items-center text-sm font-semibold text-red-700 bg-red-100 px-3 py-1 rounded-md hover:bg-red-200 transition"
                  >
                    🔍 Open Diagnostics
                  </button>
                </div>
              </div>
            )}

            {!loading && encounters.length === 0 && !error && (
              <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-12 text-center shadow-sm">
                <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center text-blue-500 mx-auto mb-6">
                  <svg
                    className="w-10 h-10"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  No Encounters Found
                </h3>
                <p className="text-gray-500 mb-8 max-w-sm mx-auto">
                  It looks like you haven't recorded any patient conversations
                  yet. Create one from the iOS app to see it here!
                </p>
                <button
                  onClick={() => navigate("/encounters/debug")}
                  className="px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-md transition-all active:scale-95"
                >
                  🔍 Run Diagnostics
                </button>
              </div>
            )}

            {encounters.length > 0 && (
              <>
                <div className="grid gap-4 mb-10">
                  {encounters.map((encounter) => (
                    <div
                      key={encounter.id}
                      onClick={() => navigate(`/encounters/${encounter.id}`)}
                      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-xl hover:border-blue-300 transition-all cursor-pointer group relative overflow-hidden"
                    >
                      <div className="absolute top-0 left-0 w-1 h-full bg-blue-600 scale-y-0 group-hover:scale-y-100 transition-transform origin-top"></div>

                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center text-blue-600 shadow-inner">
                            <span className="font-bold text-xl">
                              {encounter.patient_name
                                ? encounter.patient_name.charAt(0).toUpperCase()
                                : encounter.patient
                                  ? `P${encounter.patient}`
                                  : "U"}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-bold text-xl text-gray-900 group-hover:text-blue-700 transition-colors">
                              {encounter.patient_name ||
                                (encounter.patient
                                  ? `Patient #${encounter.patient}`
                                  : "Unknown Patient")}
                            </h3>
                            <div className="flex items-center gap-3 mt-1">
                              <p className="text-gray-400 text-sm flex items-center gap-1.5">
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                {formatDate(encounter.created_at)}
                              </p>
                              <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                              <p className="text-gray-400 text-sm flex items-center gap-1.5 font-medium">
                                <span className="text-blue-500 italic">
                                  ID: {encounter.id.substring(0, 8)}...
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {encounter.has_audio && (
                            <span
                              className="bg-blue-50 text-blue-600 border border-blue-100 rounded-full p-2"
                              title="Audio Available"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15.536 8.464a5 5 0 010 7.072M12 10v4m-3.536-5.536a5 5 0 000 7.072M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </span>
                          )}
                          <span
                            className="bg-green-50 text-green-600 border border-green-100 rounded-full p-2"
                            title="SOAP Note Ready"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                              />
                            </svg>
                          </span>
                          <div className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center text-gray-400 group-hover:bg-blue-600 group-hover:text-white transition-all">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between bg-white px-6 py-4 rounded-2xl shadow-sm border border-gray-100 mt-6">
                    <div className="flex flex-1 justify-between sm:hidden">
                      <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`font-bold px-4 py-2 border rounded-xl text-gray-700 ${
                          currentPage === 1
                            ? "opacity-30 cursor-not-allowed"
                            : "hover:bg-gray-50 active:scale-95"
                        }`}
                      >
                        Prev
                      </button>
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`font-bold px-4 py-2 border rounded-xl text-gray-700 ${
                          currentPage === totalPages
                            ? "opacity-30 cursor-not-allowed"
                            : "hover:bg-gray-50 active:scale-95"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-500 italic">
                          Showing{" "}
                          <span className="font-bold text-gray-900">
                            {(currentPage - 1) * pageSize + 1}
                          </span>{" "}
                          -{" "}
                          <span className="font-bold text-gray-900">
                            {Math.min(currentPage * pageSize, totalCount)}
                          </span>{" "}
                          of{" "}
                          <span className="font-bold text-blue-600">
                            {totalCount}
                          </span>
                        </p>
                      </div>
                      <nav className="inline-flex gap-1">
                        <button
                          onClick={goToPreviousPage}
                          disabled={currentPage === 1}
                          className={`w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 transition-all ${
                            currentPage === 1
                              ? "opacity-30 cursor-not-allowed"
                              : "hover:bg-blue-50 hover:text-blue-600"
                          }`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        </button>

                        {[...Array(totalPages)].map((_, i) => {
                          const pageNumber = i + 1;
                          if (
                            totalPages > 7 &&
                            pageNumber !== 1 &&
                            pageNumber !== totalPages &&
                            Math.abs(pageNumber - currentPage) > 2
                          ) {
                            if (Math.abs(pageNumber - currentPage) === 3) {
                              return (
                                <span
                                  key={pageNumber}
                                  className="w-10 h-10 flex items-center justify-center text-gray-400"
                                >
                                  ...
                                </span>
                              );
                            }
                            return null;
                          }

                          return (
                            <button
                              key={pageNumber}
                              onClick={() => setCurrentPage(pageNumber)}
                              className={`w-10 h-10 flex items-center justify-center rounded-xl font-bold transition-all ${
                                currentPage === pageNumber
                                  ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                                  : "border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600"
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}

                        <button
                          onClick={goToNextPage}
                          disabled={currentPage === totalPages}
                          className={`w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 transition-all ${
                            currentPage === totalPages
                              ? "opacity-30 cursor-not-allowed"
                              : "hover:bg-blue-50 hover:text-blue-600"
                          }`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
    </AppLayout>
  );
};

export default EncountersListPage;
